import { ApplicationAnalyticsEvent, ApplicationAnalyticsEventDispatchOptions, sendAnalyticsData } from '@aglenergy/agl.sales.analytics';
import { store } from 'app/shared/store/store';
import { Utag, UtagData, UtagErrorData, UtagQueueFunction } from 'app/shared/models/tealium-analytics.model';
import { ProductCatalogueProduct } from 'app/shared/models/product-catalogue-product.model';
import { ProductCategory } from 'app/shared/models/product-category.model';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';

class TealiumAnalyticService {
  private static instance: TealiumAnalyticService;
  public static get Instance(): TealiumAnalyticService {
    return this.instance || (this.instance = new this());
  }

  public get utagQueue(): UtagQueueFunction[] {
    return (window as any).utagQueue as UtagQueueFunction[];
  }

  public trackProductCards(products: ProductCatalogueProduct[], noAddress = false, productCategory: ProductCategory): void {
    const aemCIFIntegrationValueForAnalytics = store.get(StoreKeys.aemCIFIntegrationEnabled);
    try {
      const data = {
        module_product_name: products ? `${products.map((x) => x.name).join(',')}` : '',
        module_product_id: products ? `${products.map((x) => x.sku).join(',')}` : '',
        module_product_fuel: productCategory
      };
      switch (productCategory) {
        case 'nbn': {
          if (!aemCIFIntegrationValueForAnalytics) {
            const techTypes = products ? products.map((x) => x.attributes.tech_type).join() : '';
            const eventData = techTypes ? Array.from(new Set(techTypes.split(','))).toString() : '';

            if (!!products && noAddress) {
              this.createAndSendData(['No Address', 'Loaded'], eventData, data, productCategory);
            } else if (products) {
              tealiumAnalyticService.createAndSendData(['Address', 'Searched', 'Complete'], eventData, data, productCategory);
            }
          }
          break;
        }
        case 'mobile plan': {
          if (!products.length) {
            tealiumAnalyticService.createAndSendData([], '', data, productCategory);
          } else {
            tealiumAnalyticService.createAndSendData(['Plans Presented'], '', data, productCategory);
          }
          break;
        }
        default:
          break;
      }
    } catch {
      console.log('This syntax prevents errors');
    }
  }

  public trackPlanSelectionData(
    product: ProductCatalogueProduct,
    productCategory: ProductCategory,
    action: string,
    quantity?: number
  ): void {
    switch (productCategory) {
      case 'mobile plan': {
        const extraData = {
          telco_product: product.name,
          telco_product_id: product.sku,
          telco_product_data: product.attributes?.data_allowance,
          telco_product_price: product.price,
          telco_product_selected_plan_category: store.get(StoreKeys.selectedPlanCategory)
        };

        if (!quantity) {
          const context = [`Module][Plan Card][${productCategory}][${action}]`];
          this.sendEvent(context, null, product.name, extraData, productCategory);
        } else {
          const contextQtyData = [`Module][Plan Card][Sim Plan Qty][${action}`];
          this.sendEvent(contextQtyData, null, product.name, quantity.toString, productCategory);
        }

        break;
      }
      case 'nbn': {
        const extraData = {
          nbn_product: product.name,
          nbn_product_id: product.sku,
          nbn_product_price: product.price,
          nbn_product_selected_plan_category: store.get(StoreKeys.selectedPlanCategory),
          fibre_connect_nbn_plan: product.fibreUpgrade ? 'yes' : 'no'
        };

        const context = [`Module][Plan Card][${productCategory}][${action}`];
        tealiumAnalyticService.sendEvent(context, null, product.name, extraData, productCategory);
        break;
      }
      default:
        break;
    }
  }

  public trackBundleSelectionData(action: string, productCategory: ProductCategory, eventData: string[] = null): void {
    const context = [`Bundle Builder][${action}][${productCategory}][Clicked`];
    const planSelected = eventData ? { event_data: eventData.toString() } : {};
    tealiumAnalyticService.sendEvent(context, null, '', planSelected, productCategory);
  }

  public trackPlanCategoriesClick(planCategory: string, productCategory: ProductCategory): void {
    const context = [`Module][Plan Card Category][${planCategory}`];
    tealiumAnalyticService.sendEvent(context, null, '', null, productCategory);
  }

  public trackCheckYourAddressButton(action: string, productCategory: ProductCategory): void {
    const context = [`Module][Plan Card Category][${action}`];
    tealiumAnalyticService.sendEvent(context, null, '', null, productCategory);
  }

  public createAndSendData(events: string[], eventData: string, extraData: any = null, productCategory: ProductCategory): void {
    const context = productCategory === 'mobile plan' ? ['SIM plan sales', 'Compare SIM plans page'] : ['BCP', 'Product Cards'];
    if (productCategory === 'mobile plan') {
      extraData = { ...extraData, event_additional_data: '[Month To Month]' };
    }
    this.sendEvent(context, events, eventData, extraData, productCategory);
  }

  public createAndSendAddressData(events: string[], eventData: string, extraData: any = null, productCategory: ProductCategory): void {
    const context = ['BCP', 'Search', 'Address'];
    this.sendEvent(context, events, eventData, extraData, productCategory);
  }

  public trackPrefillAddress(action: string, eventData: string, productCategory: ProductCategory): void {
    const context = [`BCP][Search][Address][Prepopulated][${action}`];
    this.sendEvent(context, null, eventData, null, productCategory);
  }

  public createAndSendNavigationData(events: string[], eventData: string, extraData: any = null, productCategory: ProductCategory): void {
    const context = ['Accordion', 'item'];
    this.sendEvent(context, events, eventData, extraData, productCategory);
  }

  private get utagData(): UtagData {
    return (window as any).utag_data as UtagData;
  }

  private get baseUtagData(): any {
    return { page_name: this.utagData.page_name, page_URL: this.utagData.page_URL };
  }

  private get utag(): Utag {
    return (window as any).utag as Utag;
  }

  public sendAnalyticsError(event: string, errorCode: string, errorDescription: string, errorType: string): void {
    const utagErrorData: UtagErrorData = { ...this.baseUtagData };
    if (errorCode) {
      utagErrorData.error_code = errorCode;
    }
    if (errorDescription) {
      utagErrorData.error_description = errorDescription;
    }
    if (errorType) {
      utagErrorData.error_type = errorType;
    }
    this.utagQueue?.push({
      run: () => {
        utagErrorData.event = event;
        if (this.utag) {
          this.utag.link(utagErrorData);
        }
      }
    });
  }

  public sendEvent(
    context: string[],
    events: string[],
    eventData: string,
    extraEventData: { [key: string]: any } = {},
    productCategory: ProductCategory,
    errorData?: PropType<ApplicationAnalyticsEvent, 'error'>
  ): void {
    const contextAndEvents = events ? context.concat(events) : context;
    const dataEvent: ApplicationAnalyticsEvent = {
      context: contextAndEvents,
      eventData,
      signupFlowName: productCategory,
      extraEventData,
      error: errorData ? errorData : undefined
    };
    const sendOptions: ApplicationAnalyticsEventDispatchOptions = {
      sendOnlyNewEvents: false,
      shouldDecameliseEventNames: false,
      logEvents: false
    };
    this.sendAnalyticsData(dataEvent, sendOptions);
  }

  private sendAnalyticsData(event: ApplicationAnalyticsEvent, sendOptions: ApplicationAnalyticsEventDispatchOptions): void {
    sendAnalyticsData(event, sendOptions);
  }
}

type PropType<TObj, TProp extends keyof TObj> = TObj[TProp];

export const tealiumAnalyticService = new TealiumAnalyticService();
